import { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { Chip } from '@components/Chip';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { updateState, getChipsLabel, createUpdatedChips, isChipActive, hasActiveChips } from '@helpers/filters';
import { formatTimeframeChipValue } from '@helpers/dates';
import { FiltersIntegrationType } from '@constants/integrations';
import { GroupByFilter } from '@constants/analytics';
import { useTranslation, useLocations } from '@hooks';

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    filter,
    setFilter,
    withChips = true,
    type,
    setFilterNumber,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const [filterChips, setFilterChips] = useState({
        locations: [],
        integrations: [],
        timeframe: {},
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const { getLocationsOptions, locationsMap } = useLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            applyChips();
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.locations.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    const options = useMemo(() => {
        //locationsOptions
        const filterCb = l => l?.profiles?.length > 0 && l?.profiles.some(p => p.type in FiltersIntegrationType);
        const locationsOptions = getLocationsOptions(filterCb);

        //integrationsOptions
        const integrationsOptions = Array.from(
            new Set(
                filter.locations
                    .map(item => locationsMap[item.id]?.profiles || [])
                    .flat()
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ).map(type => ({ value: type, label: FiltersIntegrationType[type] }));

        const groupingOptions = [
            { value: GroupByFilter.Days, label: t('common.day') },
            { value: GroupByFilter.Weeks, label: t('common.week') },
            { value: GroupByFilter.Months, label: t('common.month') },
            { value: GroupByFilter.Years, label: t('common.year') },
        ];

        return {
            locations: locationsOptions,
            integrations: integrationsOptions,
            grouping: groupingOptions,
        };
    }, [filter, locationsMap]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;

            switch (key) {
                case 'timeframe':
                    increment = filterChips[key]?.start ? 1 : 0;
                    break;
                default:
                    increment = filterChips[key]?.length > 0 ? 1 : 0;
                    break;
            }

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const updateLocationSensitiveData = newLocationsIds => {
        const integrations = [
            ...new Set(
                newLocationsIds
                    .map(selectedItem => locationsMap[selectedItem])
                    ?.flatMap(location => location?.profiles)
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ];

        return { integrations };
    };

    const update = {
        locations: items => {
            const selectedValues = items.length ? items : [];
            const selectedValuesIds = selectedValues.length ? selectedValues.map(item => item.id) : [];
            const isAllIntegrationsSelected = filter.integrations.length === options.integrations.length;
            const { integrations } = updateLocationSensitiveData(selectedValuesIds);

            setFilter(prev => ({
                ...prev,
                locations: selectedValues,
                integrations: updateState(prev?.integrations, integrations, isAllIntegrationsSelected),
            }));
        },
        integrations: value => setFilter(prev => ({ ...prev, integrations: value })),
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
        grouping: e => {
            setFilter(prev => ({ ...prev, grouping: e.target.value }));
        },
    };

    const CHIPS_ACTIONS = {
        locations: {
            reset: () => update.locations(options.locations),
            value: getChipsLabel(filterChips?.locations),
        },
        integrations: {
            reset: () =>
                setFilter(prev => ({
                    ...prev,
                    integrations: updateLocationSensitiveData(prev.locations.map(l => l.id)).integrations,
                })),
            value: getChipsLabel(filterChips?.integrations),
        },
        timeframe: {
            reset: () => setFilter(prev => ({ ...prev, timeframe: { start: null, end: null } })),
            value: filterChips?.timeframe?.start
                ? formatTimeframeChipValue(filterChips.timeframe.start, filterChips.timeframe.end, language)
                : '',
        },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const resetFilters = () => Object.values(CHIPS_ACTIONS).forEach(({ reset }) => reset());

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        for (let key in filter) {
            if (Array.isArray(filter[key])) {
                formOptions.push({
                    type: key === 'locations' ? 'autocomplete' : 'multiSelect',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: !filter[key].length,
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            } else if (key === 'grouping') {
                formOptions.push({
                    type: 'select',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = Object.keys(filter).every(key => {
        if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    const hasDraftFilters = useMemo(
        () => !isValid || hasActiveChips(createUpdatedChips(filterChips, filter, options)),
        [isValid, filterChips, filter, options],
    );

    return (
        <FiltersWrapper>
            <Button
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            {withChips &&
                !isMobile &&
                Object.keys(filterChips).map(
                    key =>
                        isChipActive(filterChips[key]) && (
                            <Chip
                                key={key}
                                label={CHIPS_ACTIONS[key].value}
                                onDelete={() => {
                                    CHIPS_ACTIONS[key].reset();
                                    setRefetchFilters(true);
                                }}
                            />
                        ),
                )}
            <FiltersModal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={!hasDraftFilters}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </FiltersModal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
